/**
 * useDragAndDrop Hook
 * Provides drag-and-drop functionality for track reordering using @dnd-kit
 *
 * Features:
 * - Mouse, touch, and keyboard drag support
 * - Accessible keyboard navigation
 * - Visual feedback during drag
 * - Collision detection
 * - Integration with workbench store
 */

import { useMemo } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects,
  DropAnimation,
} from '@dnd-kit/core';
import {
  SortableContext as SortableContextComponent,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { TrackWithInstanceId } from '../api/types';
import { useWorkbenchStore } from '../stores/workbench';

/**
 * Hook for drag-and-drop configuration
 * Returns sortable context and track IDs for use within AppShell's DndContext
 */
export function useDragAndDrop() {
  const { tracks } = useWorkbenchStore();

  // Get track instanceIds for sortable context
  const trackIds = useMemo(() => tracks.map((track) => track.instanceId), [tracks]);

  return {
    SortableContext: SortableContextComponent,
    trackIds,
    sortingStrategy: verticalListSortingStrategy,
  };
}

/**
 * Hook to create sensors for drag-and-drop
 * Used by AppShell's DndContext
 */
export function useDragSensors() {
  return useSensors(
    // Pointer sensor for mouse and touch
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag (prevents accidental drags)
      },
    }),
    // Keyboard sensor for accessibility
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
}

/**
 * Drop animation configuration for smooth visual feedback
 */
export const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

/**
 * Helper to create drag overlay styles
 */
export function getDragOverlayStyles(track: TrackWithInstanceId | null) {
  if (!track) return {};

  return {
    cursor: 'grabbing',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
    transform: 'rotate(-3deg)',
    opacity: 0.95,
  };
}

/**
 * Example usage component wrapper
 *
 * ```tsx
 * function TrackList({ tracks }: { tracks: Track[] }) {
 *   const {
 *     DndContext,
 *     SortableContext,
 *     sensors,
 *     trackIds,
 *     handleDragStart,
 *     handleDragEnd,
 *     collisionDetection,
 *     sortingStrategy,
 *   } = useDragAndDrop();
 *
 *   const [activeTrack, setActiveTrack] = useState<Track | null>(null);
 *
 *   return (
 *     <DndContext
 *       sensors={sensors}
 *       collisionDetection={collisionDetection}
 *       onDragStart={(e) => {
 *         handleDragStart(e);
 *         setActiveTrack(tracks.find((t) => t.id === e.active.id) ?? null);
 *       }}
 *       onDragEnd={(e) => {
 *         handleDragEnd(e);
 *         setActiveTrack(null);
 *       }}
 *     >
 *       <SortableContext items={trackIds} strategy={sortingStrategy}>
 *         {tracks.map((track) => (
 *           <SortableTrackRow key={track.id} track={track} />
 *         ))}
 *       </SortableContext>
 *       <DragOverlay dropAnimation={dropAnimationConfig}>
 *         {activeTrack && (
 *           <div style={getDragOverlayStyles(activeTrack)}>
 *             <TrackRow track={activeTrack} isDragging />
 *           </div>
 *         )}
 *       </DragOverlay>
 *     </DndContext>
 *   );
 * }
 * ```
 */
